/**
 * lib/financeAnalysis.ts
 * ------------------------------------------------------------------
 * WHERE THE MONEY COMES FROM, AND WHERE IT GOES.
 *
 * Two questions a head asks that a ledger cannot answer on its own,
 * because a ledger is organised by account and the questions are about
 * groups of pupils and groups of suppliers.
 *
 * BILLED AND COLLECTED ARE NEVER BLENDED. This is the whole point of the
 * income half. A form that bills well and collects badly looks identical
 * to one that bills badly and collects well the moment you average them
 * — and the two need opposite responses: one is a fee-setting problem,
 * the other a collections problem. So both figures sit on every row, with
 * the collection rate between them.
 *
 * A DISCOUNT IS SHOWN, NOT NETTED. What the school forgave is a decision
 * it made, and netting it into the billed figure hides how much of a
 * form's income was given away rather than charged.
 *
 * SPENDING IS GROUPED BY WHO WAS PAID, not only by account code. "We
 * spent P240,000 on repairs" is a fact; "P180,000 of it went to one
 * contractor" is the beginning of a question.
 *
 * PURE. Documents in, groupings out.
 * ------------------------------------------------------------------ */
import { round2 } from '@/lib/financeCore';
import type { Invoice, Payment } from '@/lib/financeCore';
import type { Voucher } from '@/lib/vouchers';

export interface IncomeGroupRow {
  key: string;
  label: string;
  pupils: number;
  /** What was charged before anything was given away. */
  gross: number;
  discounts: number;
  /** gross − discounts. What the family was actually asked for. */
  billed: number;
  collected: number;
  outstanding: number;
  /** collected ÷ billed, as a percentage. The number worth ranking on. */
  collectionRate: number;
}

/**
 * Fee income by any grouping of pupils.
 *
 * `groupOf` returns the group for a pupil, so the same function serves
 * by form, by class, or by anything else the school asks for later
 * without a second implementation to keep in step.
 */
export function analyseIncome(
  invoices: Invoice[],
  payments: Payment[],
  groupOf: (studentId: string) => { key: string; label: string } | undefined,
): IncomeGroupRow[] {
  const rows = new Map<string, IncomeGroupRow & { seen: Set<string> }>();

  const rowFor = (key: string, label: string) => {
    let r = rows.get(key);
    if (!r) {
      r = {
        key, label, pupils: 0, gross: 0, discounts: 0, billed: 0,
        collected: 0, outstanding: 0, collectionRate: 0, seen: new Set(),
      };
      rows.set(key, r);
    }
    return r;
  };

  for (const inv of invoices) {
    if (inv.status === 'void') continue;
    const g = groupOf(inv.studentId);
    if (!g) continue;
    const r = rowFor(g.key, g.label);
    r.seen.add(inv.studentId);
    r.gross = round2(r.gross + (Number(inv.subtotal) || 0));
    r.discounts = round2(r.discounts + (Number(inv.discountTotal) || 0));
    r.billed = round2(r.billed + (Number(inv.total) || 0));
  }

  for (const p of payments) {
    const g = groupOf(p.studentId);
    if (!g) continue;
    const r = rowFor(g.key, g.label);
    r.collected = round2(r.collected + (Number(p.amount) || 0));
  }

  const out: IncomeGroupRow[] = [];
  for (const r of rows.values()) {
    const { seen, ...rest } = r;
    out.push({
      ...rest,
      pupils: seen.size,
      outstanding: round2(rest.billed - rest.collected),
      /* Against what was BILLED, not what was charged gross. A form given
         a large bursary has not failed to collect the part the school
         chose to forgive. */
      collectionRate: rest.billed > 0
        ? round2((rest.collected / rest.billed) * 100)
        : 0,
    });
  }

  return out.sort((a, b) => a.label.localeCompare(b.label, undefined, { numeric: true }));
}

export interface SpendRow {
  key: string;
  label: string;
  /** How many separate payments made it up. One large or many small. */
  count: number;
  total: number;
  /** Share of all spending in the period. */
  sharePct: number;
}

/**
 * Spending by payee.
 *
 * ONLY WHAT WAS ACTUALLY PAID. An approved voucher is a commitment, not
 * spending, and mixing the two answers neither "what have we spent" nor
 * "what do we owe". Creditors aging answers the second.
 */
export function analyseSpendByPayee(
  vouchers: Voucher[],
  from?: string,
  to?: string,
): SpendRow[] {
  const paid = vouchers.filter((v) => {
    if (v.currentStatus !== 'paid') return false;
    const d = (v.paidAt ?? '').slice(0, 10);
    if (from && d < from) return false;
    if (to && d > to) return false;
    return true;
  });

  const by = new Map<string, SpendRow>();
  for (const v of paid) {
    const key = v.supplierId ?? `name:${(v.payeeName || '').trim().toLowerCase()}`;
    const r = by.get(key) ?? {
      key, label: v.payeeName || 'Unnamed', count: 0, total: 0, sharePct: 0,
    };
    r.count += 1;
    r.total = round2(r.total + (Number(v.total) || 0));
    by.set(key, r);
  }

  const rows = [...by.values()];
  const grand = round2(rows.reduce((n, r) => n + r.total, 0));
  for (const r of rows) {
    r.sharePct = grand > 0 ? round2((r.total / grand) * 100) : 0;
  }

  return rows.sort((a, b) => b.total - a.total);
}

/** Spending by the account it was coded to. */
export function analyseSpendByAccount(
  vouchers: Voucher[],
  nameOf: (code: string) => string,
  from?: string,
  to?: string,
): SpendRow[] {
  const by = new Map<string, SpendRow>();
  let grand = 0;

  for (const v of vouchers) {
    if (v.currentStatus !== 'paid') continue;
    const d = (v.paidAt ?? '').slice(0, 10);
    if (from && d < from) continue;
    if (to && d > to) continue;

    /* Per LINE, not per voucher. A voucher covering three accounts
       assigned wholly to the first would misstate all three. */
    for (const l of v.lines ?? []) {
      const code = l.accountCode || '(uncoded)';
      const r = by.get(code) ?? { key: code, label: nameOf(code), count: 0, total: 0, sharePct: 0 };
      r.count += 1;
      r.total = round2(r.total + (Number(l.amount) || 0));
      by.set(code, r);
      grand = round2(grand + (Number(l.amount) || 0));
    }
  }

  const rows = [...by.values()];
  for (const r of rows) r.sharePct = grand > 0 ? round2((r.total / grand) * 100) : 0;
  return rows.sort((a, b) => b.total - a.total);
}

export interface ConcentrationWarning {
  label: string;
  total: number;
  sharePct: number;
}

/**
 * Where one payee takes an unusual share of the spending.
 *
 * Not an accusation — a school genuinely has one landlord and one bus
 * contractor. It is a question worth asking once a year, and the sort of
 * thing nobody notices from a list sorted alphabetically.
 */
export function spendConcentration(rows: SpendRow[], thresholdPct = 20): ConcentrationWarning[] {
  return rows
    .filter((r) => r.sharePct >= thresholdPct)
    .map(({ label, total, sharePct }) => ({ label, total, sharePct }));
}

export interface MonthPoint {
  month: string;
  billed: number;
  collected: number;
  spent: number;
}

/** Month by month, so a trend is visible rather than inferred. */
export function monthlyTrend(
  invoices: Invoice[],
  payments: Payment[],
  vouchers: Voucher[],
  months: string[],
): MonthPoint[] {
  return months.map((month) => {
    const billed = round2(invoices
      .filter((i) => i.status !== 'void' && (i.issuedAt ?? '').slice(0, 7) === month)
      .reduce((n, i) => n + (Number(i.total) || 0), 0));
    const collected = round2(payments
      .filter((p) => (p.receivedAt ?? '').slice(0, 7) === month)
      .reduce((n, p) => n + (Number(p.amount) || 0), 0));
    const spent = round2(vouchers
      .filter((v) => v.currentStatus === 'paid' && (v.paidAt ?? '').slice(0, 7) === month)
      .reduce((n, v) => n + (Number(v.total) || 0), 0));
    return { month, billed, collected, spent };
  });
}
